import Phaser from 'phaser';
import { eventBus, EVENTS } from '../EventBus';
import { DataByte, DATA_BYTE_VALUES } from '../entities/DataByte';
import { DigitalHazard } from '../entities/DigitalHazard';
import { PlayerPacket } from '../entities/PlayerPacket';
import { PowerUp } from '../entities/PowerUp';
import { GRID_COLUMNS, GRID_ROWS, HAZARD_STARTS, LEVELS, POWER_UP_POSITIONS, createNetworkPaths, pointKey } from '../data/levels';
import { OBJECTIVES, POWER_UP_LABELS } from '../data/lessons';
import { sameCell } from '../systems/CollisionSystem';
import { controllerManager, shouldPauseForDisconnect, type ControllerAction, type ControllerSnapshot, type InputMethod } from '../systems/ControllerSystem';
import { showComponentLesson } from '../systems/EducationSystem';
import { InputSystem } from '../systems/InputSystem';
import { LevelProgression } from '../systems/LevelProgression';
import { ScoreSystem } from '../systems/ScoreSystem';
import type { DataByteKind, Direction, GameSettings, GridPoint, HazardKind, HudState, LevelDefinition, LevelId, NetworkComponent, PowerUpKind, ScreenPayload } from '../types';

const CELL_SIZE = 50;
const ORIGIN_X = 196;
const ORIGIN_Y = 150;
const MAX_FRAME_DELTA = 50;
const HAZARD_KINDS: HazardKind[] = ['malware-bug', 'packet-loss', 'spam-bot', 'phishing-trap', 'broken-zone'];
const DIRECTIONS: { direction: Direction; delta: GridPoint }[] = [
  { direction: 'up', delta: { x: 0, y: -1 } }, { direction: 'right', delta: { x: 1, y: 0 } },
  { direction: 'down', delta: { x: 0, y: 1 } }, { direction: 'left', delta: { x: -1, y: 0 } },
];

interface GameSceneData { levelId?: LevelId; settings?: GameSettings; mode?: 'packet' | 'bonus'; community?: string }
interface ComponentVisual { ring: Phaser.GameObjects.Arc; label: Phaser.GameObjects.Text; icon: Phaser.GameObjects.Container }

export class GameScene extends Phaser.Scene {
  private level: LevelDefinition = LEVELS.quick;
  private settings!: GameSettings;
  private progression!: LevelProgression;
  private score = new ScoreSystem();
  private player!: PlayerPacket;
  private playerCell!: GridPoint;
  private inputSystem!: InputSystem;
  private paths = createNetworkPaths();
  private readonly bytes = new Map<string, DataByte>();
  private readonly powerUps = new Map<string, PowerUp>();
  private readonly componentVisuals = new Map<NetworkComponent, ComponentVisual>();
  private hazards: DigitalHazard[] = [];
  private remainingMs = 60_000;
  private durationSeconds = 60;
  private active = false;
  private nextHazardMove = 0;
  private lastHudUpdate = 0;
  private hitCooldownUntil = 0;
  private shieldUntil = 0;
  private boostUntil = 0;
  private shortcutUntil = 0;
  private freezeUntil = 0;
  private encryptionUntil = 0;
  private slowUntil = 0;
  private comboUntil = 0;
  private warnedAt = -1;
  private readonly wrongRouteKeys = new Set<string>();
  private shortcutGraphic!: Phaser.GameObjects.Graphics;
  private objectiveGraphic!: Phaser.GameObjects.Graphics;
  private heldPowerUp?: PowerUpKind;
  private paused = false;
  private pauseReason?: 'manual' | 'disconnect';
  private pauseStartedAt = 0;
  private mode: 'packet' | 'bonus' = 'packet';
  private community = 'Inside the Network';
  private readonly controllerUnsubscribers: Array<() => void> = [];

  constructor() { super('GameScene'); }

  init(data: GameSceneData): void {
    this.level = LEVELS[data.levelId ?? 'quick'];
    if (!data.settings) throw new Error('Game settings were not provided.');
    this.settings = data.settings;
    this.mode = data.mode ?? 'packet';
    this.community = data.community ?? 'Inside the Network';
    this.score = new ScoreSystem();
    this.progression = new LevelProgression(this.level.checkpoints);
    this.durationSeconds = data.settings.gameDuration;
    this.remainingMs = this.durationSeconds * 1000;
    this.active = true;
    this.nextHazardMove = this.lastHudUpdate = this.hitCooldownUntil = 0;
    this.shieldUntil = this.boostUntil = this.shortcutUntil = this.freezeUntil = this.encryptionUntil = this.slowUntil = this.comboUntil = 0;
    this.warnedAt = -1;
    this.heldPowerUp = undefined;
    this.paused = false;
    this.pauseReason = undefined;
    this.pauseStartedAt = 0;
    this.wrongRouteKeys.clear();
    this.paths = createNetworkPaths();
    this.bytes.clear(); this.powerUps.clear(); this.componentVisuals.clear(); this.hazards = [];
  }

  create(): void {
    this.drawArena();
    this.drawNetwork();
    this.objectiveGraphic = this.add.graphics().setDepth(4);
    this.drawCheckpoints();
    this.drawCollectibles();
    this.drawPowerUps();
    this.drawHazards();
    this.shortcutGraphic = this.add.graphics().setDepth(3);

    const start = this.level.checkpoints[0];
    if (!start) throw new Error('The selected level has no starting point.');
    this.playerCell = { x: start.x, y: start.y };
    const world = this.toWorld(this.playerCell);
    this.player = new PlayerPacket(this, world.x, world.y, this.settings.reducedMotion);
    this.inputSystem = new InputSystem(this, (direction) => this.movePlayer(direction), () => this.currentMoveDelay());
    this.controllerUnsubscribers.push(
      controllerManager.onAction((action) => this.handleControllerAction(action)),
      controllerManager.onState((snapshot) => this.handleControllerState(snapshot)),
      controllerManager.onInput((method) => this.handleInputMethod(method)),
      eventBus.on(EVENTS.resume, () => this.resumeGame()),
    );
    this.input.keyboard?.on('keydown-SPACE', this.handlePowerUpInput, this);
    this.input.keyboard?.on('keydown-SHIFT', this.handlePowerUpInput, this);
    this.input.keyboard?.on('keydown-E', this.handlePowerUpInput, this);
    this.input.keyboard?.on('keydown-T', this.showObjective, this);
    this.input.keyboard?.on('keydown-P', this.togglePause, this);
    this.input.keyboard?.on('keydown-ESC', this.togglePause, this);
    window.addEventListener('packet-power-up', this.handlePowerUpInput);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.detachControllerActions());

    const visit = this.progression.visit(start.id);
    if (visit.status === 'correct' || visit.status === 'complete') {
      this.score.connectComponent();
      this.markCheckpoint(start.id);
      this.time.delayedCall(260, () => showComponentLesson(start.id, start.label));
    }
    this.drawObjectiveRoute();
    eventBus.emit<ScreenPayload>(EVENTS.screen, { name: 'gameplay', level: this.level });
    eventBus.emit(EVENTS.announce, `${this.level.name}. ${this.currentObjective()}.`);
    this.emitHud();
  }

  update(time: number, delta: number): void {
    if (!this.active || this.paused) return;
    this.remainingMs = Math.max(0, this.remainingMs - Math.min(delta, MAX_FRAME_DELTA));
    if (this.remainingMs <= 0) { this.finish(false); return; }
    this.inputSystem.update(time);
    this.refreshEffects(time);
    if (this.score.combo > 0 && time > this.comboUntil) this.score.resetCombo();
    const second = Math.ceil(this.remainingMs / 1000);
    if (second <= 10 && second !== this.warnedAt) {
      this.warnedAt = second;
      eventBus.emit(EVENTS.effect, second <= 3 ? 'warning' : 'countdown');
      if (second <= 3) this.cameras.main.flash(80, 255, 95, 109, false);
    }
    if (time >= this.nextHazardMove && time >= this.freezeUntil) {
      const interval = this.settings.difficulty === 'easy' ? 720 : this.settings.difficulty === 'expert' ? 430 : 560;
      this.nextHazardMove = time + interval;
      this.hazards.forEach((hazard) => hazard.move(this.paths, (point) => this.toWorld(point), this.settings.reducedMotion, this.playerCell));
      this.checkHazardCollision(time);
    }
    if (time - this.lastHudUpdate > 100) { this.lastHudUpdate = time; this.emitHud(); }
  }

  private drawArena(): void {
    this.cameras.main.setBackgroundColor('#020916');
    const g = this.add.graphics();
    g.fillGradientStyle(0x020916, 0x06152f, 0x0b2752, 0x06152f, 1).fillRect(0, 0, 1600, 900);
    g.fillStyle(0x031026, 0.98).fillRoundedRect(34, 82, 1532, 764, 28);
    const zones = [
      { x: 54, w: 250, name: 'HOME', color: 0x0b315d }, { x: 304, w: 220, name: 'LOCAL ACCESS', color: 0x0a2850 },
      { x: 524, w: 220, name: 'NEIGHBORHOOD', color: 0x0b315d }, { x: 744, w: 245, name: 'INTERNET', color: 0x102d5e },
      { x: 989, w: 210, name: 'SECURITY', color: 0x0a2850 }, { x: 1199, w: 235, name: 'DATA CENTER', color: 0x0b315d },
      { x: 1434, w: 112, name: 'SAFE', color: 0x071b3b },
    ];
    zones.forEach((zone, index) => {
      g.fillStyle(zone.color, 0.52).fillRect(zone.x, 104, zone.w, 688);
      g.lineStyle(2, index === 4 ? 0xff8a34 : 0x168cff, 0.16).lineBetween(zone.x + zone.w, 104, zone.x + zone.w, 792);
      this.add.text(zone.x + 14, 768, zone.name, { fontFamily: 'Arial Black, Arial', fontSize: zone.w < 130 ? '9px' : '11px', color: index === 4 ? '#ffb37a' : '#8fdfff', letterSpacing: 2 }).setDepth(2);
    });
    this.drawCityDetails(g);
    this.add.text(68, 104, this.level.eyebrow.toUpperCase(), { fontFamily: 'Arial Black, Arial', fontSize: '13px', color: '#8fdfff', letterSpacing: 2 });
    this.add.text(1528, 104, 'LIVE NETWORK CITY', { fontFamily: 'Arial Black, Arial', fontSize: '12px', color: '#ffb37a', letterSpacing: 2 }).setOrigin(1, 0);
  }

  private drawCityDetails(g: Phaser.GameObjects.Graphics): void {
    g.lineStyle(3, 0x168cff, 0.22);
    for (let x = 70; x < 1430; x += 72) g.lineBetween(x, 735, x + 32, 698);
    g.fillStyle(0x071b3b, 0.9);
    const structures: Array<[number, number, number, number]> = [[75, 610, 120, 130], [225, 650, 70, 90], [575, 625, 130, 115], [785, 655, 170, 85], [1235, 575, 165, 165]];
    structures.forEach(([x, y, w, h]) => g.fillRoundedRect(x, y, w, h, 8));
    g.lineStyle(2, 0x1ed8ff, 0.2);
    for (let x = 92; x < 1380; x += 45) g.strokeRoundedRect(x, 680 + (x % 3) * 11, 17, 11, 2);
    g.lineStyle(4, 0xff8a34, 0.32).strokeRoundedRect(1030, 555, 125, 185, 12);
  }

  private drawNetwork(): void {
    const cable = this.add.graphics().setDepth(2);
    cable.lineStyle(18, this.settings.highContrast ? 0x1ed8ff : 0x168cff, this.settings.highContrast ? 0.5 : 0.2);
    this.forEachConnection((from, to) => cable.lineBetween(from.x, from.y, to.x, to.y));
    cable.lineStyle(3, 0x1ed8ff, this.settings.highContrast ? 0.8 : 0.34);
    this.forEachConnection((from, to) => cable.lineBetween(from.x, from.y, to.x, to.y));
    cable.fillStyle(0x1ed8ff, 0.38);
    this.paths.forEach((key) => { const world = this.toWorld(this.pointFromKey(key)); cable.fillCircle(world.x, world.y, 5); });
    if (!this.settings.reducedMotion) {
      [0, 1, 2, 3].forEach((index) => {
        const pulse = this.add.circle(ORIGIN_X, ORIGIN_Y + [2, 4, 6, 8][index]! * CELL_SIZE, 4, 0xffffff, 0.8).setDepth(3);
        this.tweens.add({ targets: pulse, x: ORIGIN_X + 22 * CELL_SIZE, duration: 2800 + index * 350, delay: index * 240, repeat: -1, ease: 'Linear' });
      });
    }
  }

  private drawCheckpoints(): void {
    this.level.checkpoints.forEach((checkpoint) => {
      const world = this.toWorld(checkpoint);
      const ring = this.add.circle(world.x, world.y, 36, 0x06152f, 1).setStrokeStyle(5, checkpoint.id === 'server' ? 0xff8a34 : 0x1ed8ff, 1).setDepth(16);
      const icon = this.drawComponentIcon(checkpoint.id, world.x, world.y).setDepth(17);
      const label = this.add.text(world.x, world.y + 43, checkpoint.shortLabel, {
        fontFamily: 'Arial Black, Arial', fontSize: '10px', color: '#ffffff', backgroundColor: '#020916e8', padding: { x: 6, y: 3 },
      }).setOrigin(0.5, 0).setDepth(18);
      this.componentVisuals.set(checkpoint.id, { ring, label, icon });
    });
  }

  private drawComponentIcon(kind: NetworkComponent, x: number, y: number): Phaser.GameObjects.Container {
    const c = this.add.container(x, y);
    const g = this.add.graphics();
    g.lineStyle(3, 0xffffff, 1);
    if (kind === 'device') { g.strokeRoundedRect(-14, -19, 28, 34, 4); g.lineBetween(-7, 21, 7, 21); g.lineBetween(0, 15, 0, 21); }
    if (kind === 'router') { g.strokeRoundedRect(-18, 1, 36, 15, 4); g.lineBetween(-11, 1, -11, -13); g.lineBetween(11, 1, 11, -13); g.strokeCircle(-11, -16, 3); g.strokeCircle(11, -16, 3); }
    if (kind === 'switch') { g.strokeRoundedRect(-20, -12, 40, 24, 4); [-12, -4, 4, 12].forEach((px) => g.strokeRect(px - 2, -2, 4, 5)); }
    if (kind === 'cable') { g.lineBetween(-20, 5, 9, 5); g.strokeRect(8, -4, 13, 18); g.lineBetween(12, -4, 12, -10); g.lineBetween(17, -4, 17, -10); }
    if (kind === 'internet') { g.strokeCircle(0, 0, 19); g.strokeEllipse(0, 0, 16, 38); g.lineBetween(-18, 0, 18, 0); g.lineBetween(-15, -9, 15, -9); g.lineBetween(-15, 9, 15, 9); }
    if (kind === 'firewall') { g.fillStyle(0xff8a34, 0.75).fillRoundedRect(-20, -19, 40, 38, 5); g.lineStyle(2, 0xffffff, 0.9); for (let row = -12; row <= 10; row += 11) { g.lineBetween(-16, row, 16, row); g.lineBetween(row % 2 ? -4 : 6, row, row % 2 ? -4 : 6, row + 10); } }
    if (kind === 'server') { g.strokeRoundedRect(-18, -22, 36, 44, 4); [-13, 0, 13].forEach((py) => { g.lineBetween(-11, py, 11, py); g.fillStyle(0xff8a34, 1).fillCircle(11, py - 5, 2); }); }
    c.add(g);
    if (kind === 'server' && this.textures.exists('turncom360-mark')) c.add(this.add.image(0, -2, 'turncom360-mark').setDisplaySize(22, 19).setAlpha(0.72));
    return c;
  }

  private drawCollectibles(): void {
    const hazardCount = this.hazardCount();
    const blocked = new Set([...this.level.checkpoints.map(pointKey), ...POWER_UP_POSITIONS.map(({ point }) => pointKey(point)), ...HAZARD_STARTS.slice(0, hazardCount).map(pointKey)]);
    let index = 0;
    for (const key of this.paths) {
      const point = this.pointFromKey(key);
      if (blocked.has(key) || (point.x * 2 + point.y) % 3 !== 0) continue;
      const kind: DataByteKind = index % 17 === 0 ? 'security-key' : index % 11 === 0 ? 'bonus' : index % 7 === 0 ? 'bandwidth-token' : 'standard';
      const world = this.toWorld(point);
      this.bytes.set(key, new DataByte(this, kind, point, world.x, world.y, this.settings.reducedMotion));
      index += 1;
    }
  }

  private drawPowerUps(): void {
    POWER_UP_POSITIONS.forEach(({ kind, point }) => { const world = this.toWorld(point); this.powerUps.set(pointKey(point), new PowerUp(this, kind, point, world.x, world.y, this.settings.reducedMotion)); });
  }

  private drawHazards(): void {
    this.hazards = HAZARD_STARTS.slice(0, this.hazardCount()).map((point, index) => {
      const world = this.toWorld(point);
      return new DigitalHazard(this, HAZARD_KINDS[index] ?? 'malware-bug', point, world.x, world.y, this.settings.reducedMotion);
    });
  }

  private hazardCount(): number {
    return Math.max(0, Math.min(HAZARD_STARTS.length, this.level.hazards + (this.settings.difficulty === 'expert' ? 1 : this.settings.difficulty === 'easy' ? -1 : 0)));
  }

  private movePlayer(direction: Direction): boolean {
    if (!this.active || this.paused) return false;
    const delta = DIRECTIONS.find((item) => item.direction === direction)?.delta;
    if (!delta) return false;
    const target = { x: this.playerCell.x + delta.x, y: this.playerCell.y + delta.y };
    if (!this.paths.has(pointKey(target))) return false;
    this.playerCell = target;
    const world = this.toWorld(target);
    this.player.face(direction);
    if (this.settings.reducedMotion) this.player.setPosition(world.x, world.y);
    else this.tweens.add({ targets: this.player, x: world.x, y: world.y, duration: Math.min(112, this.currentMoveDelay() - 12), ease: 'Cubic.Out' });
    this.drawObjectiveRoute();
    this.checkCell();
    return true;
  }

  private checkCell(): void {
    const key = pointKey(this.playerCell);
    const byte = this.bytes.get(key);
    if (byte) {
      const value = DATA_BYTE_VALUES[byte.kind];
      byte.destroy(); this.bytes.delete(key); this.score.collectByte(value); this.comboUntil = this.time.now + 2_400;
      this.player.reactCollect(); this.floatPoints(this.score.lastAwarded); this.burst(this.player.x, this.player.y, byte.kind === 'bonus' ? 0xff8a34 : 0x1ed8ff);
      eventBus.emit(EVENTS.effect, 'byte');
    }
    const powerUp = this.powerUps.get(key);
    if (powerUp) {
      this.heldPowerUp = powerUp.kind;
      powerUp.destroy(); this.powerUps.delete(key);
      this.pulse(38, 0.12, 0.3);
      eventBus.emit(EVENTS.effect, 'power-up');
      eventBus.emit(EVENTS.lesson, { title: `${POWER_UP_LABELS[powerUp.kind].name.toUpperCase()} READY`, body: 'Press Square / X or the power-up button to activate it.' });
      eventBus.emit(EVENTS.announce, `${POWER_UP_LABELS[powerUp.kind].name} ready.`);
    }
    const checkpoint = this.level.checkpoints.find((item) => sameCell(item, this.playerCell));
    if (checkpoint) this.visitCheckpoint(checkpoint.id);
    this.checkHazardCollision(this.time.now);
    this.emitHud();
  }

  private visitCheckpoint(component: NetworkComponent): void {
    const result = this.progression.visit(component);
    if (result.status === 'already') return;
    if (result.status === 'out-of-order') {
      const wrongKey = `${this.progression.next?.id ?? 'none'}:${component}`;
      if (!this.wrongRouteKeys.has(wrongKey)) { this.wrongRouteKeys.add(wrongKey); this.score.registerWrongRoute(); eventBus.emit(EVENTS.announce, `Wrong route. ${this.currentObjective()}.`); }
      return;
    }
    const checkpoint = result.checkpoint;
    if (!checkpoint) return;
    this.score.connectComponent(); this.markCheckpoint(component); showComponentLesson(component, checkpoint.label);
    this.pulse(component === 'server' ? 145 : 42, component === 'server' ? 0.32 : 0.12, component === 'server' ? 0.62 : 0.34);
    if (component === 'router') eventBus.emit(EVENTS.effect, 'router');
    if (component === 'internet') eventBus.emit(EVENTS.effect, 'internet');
    if (component === 'firewall') eventBus.emit(EVENTS.effect, 'firewall');
    if (component === 'server') eventBus.emit(EVENTS.effect, 'server');
    this.cameras.main.flash(100, component === 'server' ? 255 : 30, component === 'server' ? 138 : 216, component === 'server' ? 52 : 255, false);
    this.drawObjectiveRoute();
    if (result.status === 'complete') { eventBus.emit(EVENTS.effect, 'win'); this.finish(true); }
  }

  private markCheckpoint(component: NetworkComponent): void {
    const visual = this.componentVisuals.get(component);
    if (!visual) return;
    visual.ring.setFillStyle(component === 'server' ? 0xff8a34 : 0x168cff, 1).setStrokeStyle(6, 0xffffff, 1);
    visual.label.setBackgroundColor(component === 'server' ? '#ff6b2a' : '#168cff');
    this.tweens.add({ targets: [visual.ring, visual.icon], scale: 1.3, duration: 150, yoyo: true, ease: 'Back.Out' });
  }

  private activatePowerUp(kind: PowerUpKind): void {
    const now = this.time.now;
    this.score.usePowerUp();
    if (kind === 'firewall-shield') this.shieldUntil = now + 9_000;
    if (kind === 'bandwidth-boost') this.boostUntil = now + 7_000;
    if (kind === 'wifi-extender') { this.shortcutUntil = now + 12_000; this.paths = createNetworkPaths(true); this.drawShortcut(true); }
    if (kind === 'tech-support') this.freezeUntil = now + 6_000;
    if (kind === 'encryption') this.encryptionUntil = now + 8_000;
    this.refreshEffects(now); this.burst(this.player.x, this.player.y, 0xff8a34, 14);
    eventBus.emit(EVENTS.effect, 'power-up'); eventBus.emit(EVENTS.lesson, { title: POWER_UP_LABELS[kind].name.toUpperCase(), body: POWER_UP_LABELS[kind].cue });
    eventBus.emit(EVENTS.announce, `${POWER_UP_LABELS[kind].name}. ${POWER_UP_LABELS[kind].cue}`);
    this.pulse(kind === 'firewall-shield' ? 72 : 52, kind === 'firewall-shield' ? 0.3 : 0.16, 0.48);
  }

  private refreshEffects(time: number): void {
    if (this.shortcutUntil > 0 && time >= this.shortcutUntil) { this.shortcutUntil = 0; this.paths = createNetworkPaths(false); this.drawShortcut(false); }
    this.player.setProtected(this.shieldUntil > time);
    this.player.setEncrypted(this.encryptionUntil > time);
    this.player.setBoosted(this.boostUntil > time);
    this.hazards.forEach((hazard) => hazard.setAlpha(this.freezeUntil > time ? 0.35 : 1));
  }

  private checkHazardCollision(time: number): void {
    if (time < this.hitCooldownUntil) return;
    const hazard = this.hazards.find((item) => sameCell(item.cell, this.playerCell));
    if (!hazard) return;
    if (this.encryptionUntil > time) { eventBus.emit(EVENTS.announce, 'Encryption hid Pax from the hazard.'); return; }
    if (this.shieldUntil > time) { this.shieldUntil = 0; this.player.setProtected(false); this.burst(this.player.x, this.player.y, 0xff8a34, 12); eventBus.emit(EVENTS.announce, 'Firewall Shield blocked the hazard.'); return; }
    this.hitCooldownUntil = time + 1_400;
    this.score.registerHit(); this.player.connectionLost(); this.cameras.main.shake(145, 0.006);
    this.pulse(105, 0.48, 0.65);
    eventBus.emit(EVENTS.effect, 'disconnect');
    if (hazard.kind === 'packet-loss') {
      this.slowUntil = time + 3_000;
      eventBus.emit(EVENTS.lesson, { title: 'PACKET LOSS', body: 'Traffic slowed. Keep the packet moving.' });
      return;
    }
    this.remainingMs = Math.max(0, this.remainingMs - (hazard.kind === 'phishing-trap' ? 5_000 : 3_000));
    eventBus.emit(EVENTS.lesson, { title: hazard.kind === 'phishing-trap' ? 'PHISHING TRAP' : 'CONNECTION LOST', body: 'Pax is safely rerouting from the device.' });
    const start = this.level.checkpoints[0];
    if (!start) return;
    this.playerCell = { x: start.x, y: start.y };
    const world = this.toWorld(start); this.player.setPosition(world.x, world.y); this.drawObjectiveRoute();
  }

  private finish(completed: boolean): void {
    if (!this.active) return;
    this.active = false;
    const remainingSeconds = this.remainingMs / 1000;
    const result = this.score.finish(this.level.id, completed, this.durationSeconds - remainingSeconds, remainingSeconds, this.progression.correctConnections, this.progression.totalConnections);
    result.mode = this.mode;
    result.community = this.community;
    if (completed) { this.player.celebrate(); this.connectionWave(); }
    else this.player.connectionLost();
    this.time.delayedCall(completed ? 1_050 : 650, () => this.scene.start('ResultsScene', { result }));
  }

  private emitHud(): void {
    const now = this.time.now;
    const effects: string[] = [];
    if (this.shieldUntil > now) effects.push('SHIELD'); if (this.boostUntil > now) effects.push('BOOST');
    if (this.shortcutUntil > now) effects.push('SHORTCUT'); if (this.freezeUntil > now) effects.push('TECH SUPPORT');
    if (this.encryptionUntil > now) effects.push('ENCRYPTED'); if (this.slowUntil > now) effects.push('SLOWED');
    eventBus.emit<HudState>(EVENTS.hud, {
      score: this.score.score, remainingSeconds: Math.ceil(this.remainingMs / 1000), nextLabel: this.progression.next?.shortLabel ?? 'DELIVERED',
      routeProgress: this.level.checkpoints.map((checkpoint) => checkpoint.shortLabel), correctConnections: this.progression.correctConnections,
      activeEffects: effects, combo: this.score.combo, objective: this.currentObjective(), objectiveDirection: this.objectiveDirection(),
      currentPowerUp: this.heldPowerUp ? POWER_UP_LABELS[this.heldPowerUp].name.toUpperCase() : 'NONE', fps: this.settings.showFps ? Math.round(this.game.loop.actualFps) : undefined,
    });
  }

  private currentObjective(): string { return this.progression.next ? OBJECTIVES[this.progression.next.id] : 'DELIVERY COMPLETE'; }

  private objectiveDirection(): string {
    const next = this.progression.next;
    if (!next) return 'CONNECTED';
    const dx = next.x - this.playerCell.x; const dy = next.y - this.playerCell.y;
    if (Math.abs(dx) >= Math.abs(dy) && dx !== 0) return `${dx > 0 ? 'EAST' : 'WEST'} ${Math.abs(dx)}`;
    if (dy !== 0) return `${dy > 0 ? 'SOUTH' : 'NORTH'} ${Math.abs(dy)}`;
    return 'HERE';
  }

  private drawObjectiveRoute(): void {
    if (!this.objectiveGraphic) return;
    this.objectiveGraphic.clear();
    const target = this.progression.next;
    if (!target) return;
    const route = this.findRoute(this.playerCell, target);
    if (route.length < 2) return;
    this.objectiveGraphic.lineStyle(11, 0xff8a34, 0.42);
    for (let i = 0; i < route.length - 1; i += 1) { const from = this.toWorld(route[i]!); const to = this.toWorld(route[i + 1]!); this.objectiveGraphic.lineBetween(from.x, from.y, to.x, to.y); }
    this.objectiveGraphic.lineStyle(3, 0xffffff, 0.8);
    for (let i = 0; i < route.length - 1; i += 1) { const from = this.toWorld(route[i]!); const to = this.toWorld(route[i + 1]!); this.objectiveGraphic.lineBetween(from.x, from.y, to.x, to.y); }
  }

  private findRoute(start: GridPoint, target: GridPoint): GridPoint[] {
    const startKey = pointKey(start); const targetKey = pointKey(target); const queue = [startKey]; const previous = new Map<string, string>(); const visited = new Set([startKey]);
    while (queue.length) {
      const currentKey = queue.shift(); if (!currentKey || currentKey === targetKey) break;
      const current = this.pointFromKey(currentKey);
      DIRECTIONS.forEach(({ delta }) => { const next = { x: current.x + delta.x, y: current.y + delta.y }; const key = pointKey(next); if (this.paths.has(key) && !visited.has(key)) { visited.add(key); previous.set(key, currentKey); queue.push(key); } });
    }
    if (!visited.has(targetKey)) return [];
    const route: GridPoint[] = []; let current = targetKey;
    while (true) { route.unshift(this.pointFromKey(current)); if (current === startKey) break; const prior = previous.get(current); if (!prior) return []; current = prior; }
    return route;
  }

  private floatPoints(points: number): void {
    const label = this.add.text(this.player.x, this.player.y - 42, `+${points}${this.score.combo >= 5 ? `  x${Math.min(4, 1 + Math.floor((this.score.combo - 1) / 5))}` : ''}`, { fontFamily: 'Arial Black, Arial', fontSize: '18px', color: points >= 30 ? '#ffb37a' : '#ffffff', stroke: '#06152f', strokeThickness: 5 }).setOrigin(0.5).setDepth(40);
    this.tweens.add({ targets: label, y: label.y - 48, alpha: 0, duration: 650, ease: 'Cubic.Out', onComplete: () => label.destroy() });
  }

  private burst(x: number, y: number, color: number, count = 8): void {
    if (this.settings.reducedMotion) return;
    for (let i = 0; i < count; i += 1) { const p = this.add.circle(x, y, Phaser.Math.Between(2, 5), color, 0.9).setDepth(39); const angle = (Math.PI * 2 * i) / count; this.tweens.add({ targets: p, x: x + Math.cos(angle) * Phaser.Math.Between(28, 54), y: y + Math.sin(angle) * Phaser.Math.Between(28, 54), alpha: 0, duration: 420, onComplete: () => p.destroy() }); }
  }

  private connectionWave(): void {
    if (this.settings.reducedMotion) return;
    const wave = this.add.circle(this.player.x, this.player.y, 24, 0x1ed8ff, 0).setStrokeStyle(8, 0x1ed8ff, 1).setDepth(38);
    this.tweens.add({ targets: wave, scale: 8, alpha: 0, duration: 780, ease: 'Cubic.Out', onComplete: () => wave.destroy() });
    this.cameras.main.flash(180, 30, 216, 255, false);
  }

  private drawShortcut(active: boolean): void {
    this.shortcutGraphic.clear(); if (!active) return;
    const from = this.toWorld({ x: 15, y: 8 }); const to = this.toWorld({ x: 15, y: 10 });
    this.shortcutGraphic.lineStyle(18, 0xff8a34, 0.45).lineBetween(from.x, from.y, to.x, to.y);
    this.shortcutGraphic.lineStyle(4, 0xffffff, 0.85).lineBetween(from.x, from.y, to.x, to.y);
  }

  private forEachConnection(callback: (from: { x: number; y: number }, to: { x: number; y: number }) => void): void {
    this.paths.forEach((key) => { const point = this.pointFromKey(key); const from = this.toWorld(point); [{ x: point.x + 1, y: point.y }, { x: point.x, y: point.y + 1 }].forEach((neighbor) => { if (this.paths.has(pointKey(neighbor))) callback(from, this.toWorld(neighbor)); }); });
  }

  private readonly activateHeldPowerUp = (requiredKind?: PowerUpKind): void => {
    if (!this.active || this.paused) return;
    const kind = this.heldPowerUp;
    if (!kind || (requiredKind && kind !== requiredKind)) {
      const requested = requiredKind ? POWER_UP_LABELS[requiredKind].name : 'power-up';
      eventBus.emit(EVENTS.announce, `${requested} is not available yet.`);
      return;
    }
    this.heldPowerUp = undefined;
    this.activatePowerUp(kind);
    this.emitHud();
  };

  private readonly handlePowerUpInput = (): void => this.activateHeldPowerUp();

  private handleControllerAction(action: ControllerAction): void {
    if (!this.active) return;
    if (action === 'pause') { this.togglePause(); return; }
    if (this.paused) return;
    if (action === 'power-up') this.activateHeldPowerUp();
    if (action === 'boost') this.activateHeldPowerUp('bandwidth-boost');
    if (action === 'shield') this.activateHeldPowerUp('firewall-shield');
    if (action === 'objective') this.showObjective();
  }

  private handleControllerState(snapshot: ControllerSnapshot): void {
    if (!this.active) return;
    if (snapshot.status === 'disconnected' && shouldPauseForDisconnect('gameplay', snapshot.connected, snapshot.lastInput, true)) this.pauseGame('disconnect');
    if (this.paused && this.pauseReason === 'disconnect' && snapshot.connected) this.resumeGame();
  }

  private handleInputMethod(method: InputMethod): void {
    if (this.paused && this.pauseReason === 'disconnect' && method !== 'gamepad') this.resumeGame();
  }

  private readonly togglePause = (): void => {
    if (!this.active) return;
    if (this.paused) this.resumeGame(); else this.pauseGame('manual');
  };

  private pauseGame(reason: 'manual' | 'disconnect'): void {
    if (this.paused) return;
    this.paused = true; this.pauseReason = reason; this.pauseStartedAt = this.time.now;
    eventBus.emit(EVENTS.pause, { reason, snapshot: controllerManager.getSnapshot() });
    eventBus.emit(EVENTS.announce, reason === 'disconnect' ? 'Controller disconnected. The timer is paused.' : 'Game paused.');
  }

  private resumeGame(): void {
    if (!this.paused || !this.active) return;
    const pausedFor = Math.max(0, this.time.now - this.pauseStartedAt);
    const shift = (value: number): number => value > 0 ? value + pausedFor : value;
    this.nextHazardMove = shift(this.nextHazardMove); this.hitCooldownUntil = shift(this.hitCooldownUntil);
    this.shieldUntil = shift(this.shieldUntil); this.boostUntil = shift(this.boostUntil); this.shortcutUntil = shift(this.shortcutUntil);
    this.freezeUntil = shift(this.freezeUntil); this.encryptionUntil = shift(this.encryptionUntil); this.slowUntil = shift(this.slowUntil); this.comboUntil = shift(this.comboUntil);
    this.paused = false; this.pauseReason = undefined; this.pauseStartedAt = 0;
    eventBus.emit(EVENTS.resume, undefined);
    eventBus.emit(EVENTS.announce, 'Run resumed.');
  }

  private readonly showObjective = (): void => {
    eventBus.emit(EVENTS.objective, { objective: this.currentObjective(), direction: this.objectiveDirection() });
    eventBus.emit(EVENTS.announce, `${this.currentObjective()}. Route ${this.objectiveDirection()}.`);
  };

  private pulse(duration: number, strongMagnitude: number, weakMagnitude: number): void {
    if (this.settings.controllerVibration) void controllerManager.vibrate(duration, strongMagnitude, weakMagnitude);
  }

  private detachControllerActions(): void {
    this.controllerUnsubscribers.splice(0).forEach((unsubscribe) => unsubscribe());
    this.input.keyboard?.off('keydown-SPACE', this.handlePowerUpInput, this);
    this.input.keyboard?.off('keydown-SHIFT', this.handlePowerUpInput, this);
    this.input.keyboard?.off('keydown-E', this.handlePowerUpInput, this);
    this.input.keyboard?.off('keydown-T', this.showObjective, this);
    this.input.keyboard?.off('keydown-P', this.togglePause, this);
    this.input.keyboard?.off('keydown-ESC', this.togglePause, this);
    window.removeEventListener('packet-power-up', this.handlePowerUpInput);
  }

  private currentMoveDelay(): number { if (this.slowUntil > this.time.now) return 230; return this.boostUntil > this.time.now ? 78 : 145; }
  private pointFromKey(key: string): GridPoint { const [x, y] = key.split(',').map(Number); return { x: x ?? 0, y: y ?? 0 }; }
  private toWorld(point: GridPoint): { x: number; y: number } { return { x: ORIGIN_X + point.x * CELL_SIZE, y: ORIGIN_Y + point.y * CELL_SIZE }; }
}

export const GAME_WIDTH = 1600;
export const GAME_HEIGHT = 900;
export const GRID_SIZE = { columns: GRID_COLUMNS, rows: GRID_ROWS };
